import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  Check,
  CheckCircle2,
  Loader2,
  MessageSquareWarning,
  Send,
  X,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { VoiceInterview } from "@/components/VoiceInterview";
import {
  useFileStances,
  useReviewTopic,
  useSendMessage,
  useStartTopic,
  useTopic,
  useTopics,
} from "@/lib/usePreferences";
import { useVoiceConfig, useVoiceTransport } from "@/lib/useVoice";
import { useVoiceSession } from "@/lib/voice/useVoiceSession";
import type {
  DecisionPoint,
  MessageResult,
  PreferenceSignal,
  StanceDecisionKind,
  TopicReview,
  TopicStatus,
} from "@/lib/types";
import { cn } from "@/lib/utils";

// Stances in the words a person would use (the backend's ``STANCE_WORDS``).
const STANCE_WORDS: Record<string, string> = {
  keep: "wants this",
  rules_out: "rules this out",
  open_to_alternatives: "open either way",
  depends: "depends on the case",
  prefer_change: "wants a change",
  unsure: "unsure",
};

const STATUS_LABEL: Record<TopicStatus, string> = {
  not_started: "Not started",
  in_progress: "In progress",
  done: "Done",
};

/** The label of the listed option nearest a stance in someone's own words
 *  (``fit: "loose"``), or undefined. */
function makeClosestLabeler(
  decisionPoints: DecisionPoint[],
): (s: PreferenceSignal) => string | undefined {
  const optionLabels = new Map(
    decisionPoints.flatMap((dp) => dp.options).map((o) => [o.key, o.label]),
  );
  return (s) =>
    s.fit === "loose" && s.nearest_option_key
      ? optionLabels.get(s.nearest_option_key)
      : undefined;
}

/** Build a signal → human-friendly label resolver: the option the signal is
 *  about (seeded label or the user-named one), falling back to the
 *  decision-point title. The lookup maps are built once per call — memoize the
 *  returned closure rather than calling this per signal. */
function makeSignalLabeler(
  decisionPoints: DecisionPoint[],
): (s: PreferenceSignal) => string {
  // Every option, retired included: the cards below hide retired ones, but a
  // stance captured before an option was withdrawn must still show its label
  // rather than the raw slug.
  const optionLabels = new Map(
    decisionPoints.flatMap((dp) => dp.options).map((o) => [o.key, o.label]),
  );
  const dpTitles = new Map(decisionPoints.map((dp) => [dp.key, dp.title]));
  return (s) =>
    // An option_key missing from the taxonomy (drift / model junk) falls back
    // to the raw key, not the decision-point title — two drifted options under
    // one decision point must stay distinguishable.
    (s.option_key && (optionLabels.get(s.option_key) || s.option_key)) ||
    s.named_option ||
    dpTitles.get(s.decision_point_key) ||
    s.decision_point_key;
}

function ConvictionPips({ value }: { value?: number }) {
  const n = value ?? 0;
  return (
    <span className="flex items-center gap-0.5" title={`Conviction ${n}/5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span
          key={i}
          className={cn(
            "h-1.5 w-1.5 rounded-pill",
            i <= n ? "bg-bw-blue" : "bg-bw-blue-muted/40",
          )}
        />
      ))}
    </span>
  );
}

function OptionsBlock({
  decisionPoints,
  topicTitle,
  topicFrame,
}: {
  decisionPoints: DecisionPoint[];
  topicTitle: string;
  topicFrame?: string;
}) {
  return (
    <div className="flex flex-col gap-4">
      {decisionPoints.map((dp) => {
        // Pilot topics have a single decision point titled like the topic
        // itself — repeating it under the page h1 reads as a glitch. Promote
        // the prompt to the card title instead; multi-DP topics with distinct
        // titles render normally.
        const dedupe =
          (dp.title ?? "").trim().toLowerCase() ===
          (topicTitle ?? "").trim().toLowerCase();
        return (
          <Card key={dp.key}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                {dedupe ? dp.prompt : dp.title}
              </CardTitle>
              {/* A decision point about a different actor than its topic
                  (the AI-review point inside code review) says so here. */}
              {dp.frame && dp.frame !== (topicFrame ?? "none") && dp.frame_label && (
                <p className="font-sans text-xs font-semibold uppercase tracking-wide text-bw-blue">
                  About: {dp.frame_label}
                </p>
              )}
              {!dedupe && (
                <p className="font-sans text-sm text-bw-ink-muted">
                  {dp.prompt}
                </p>
              )}
              {dp.example && (
                <p className="font-sans text-xs italic text-bw-ink-muted">
                  For example: {dp.example}
                </p>
              )}
            </CardHeader>
            <CardContent className="grid gap-3 pt-0 sm:grid-cols-2 lg:grid-cols-3">
              {dp.options
                .filter((opt) => !opt.retired)
                .map((opt) => (
                  <div
                    key={opt.key}
                    className={cn(
                      "flex flex-col gap-2 rounded-submenu border p-3",
                      // A status-quo option describes what we already do; it is
                      // not a peer choice. Dashed and unfilled so it reads as a
                      // note about the present rather than something on offer —
                      // rendering it identically to a real option is how a young
                      // codebase's accidents start collecting endorsements.
                      opt.status_quo
                        ? "border-dashed border-bw-ink-muted/40"
                        : opt.currently_used
                          ? "border-bw-blue bg-bw-blue/5"
                          : "border-bw-blue-muted/40",
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className={cn(
                          "font-sans text-sm font-semibold",
                          opt.status_quo ? "text-bw-ink-muted" : "text-bw-navy",
                        )}
                      >
                        {opt.label}
                      </span>
                      {opt.status_quo ? (
                        <span
                          className="shrink-0 rounded-pill bg-bw-warning/15 px-2 py-0.5 font-sans text-[10px] font-semibold text-bw-navy"
                          title="This describes what we do today. It is not a recommendation, and not a default."
                        >
                          what we do today
                        </span>
                      ) : (
                        opt.currently_used && (
                          <span className="shrink-0 rounded-pill bg-bw-blue/10 px-2 py-0.5 font-sans text-[10px] font-semibold text-bw-navy">
                            your stack today
                          </span>
                        )
                      )}
                    </div>
                    {opt.pros.length > 0 && (
                      <p className="font-sans text-xs text-bw-ink-muted">
                        <span className="font-semibold text-bw-navy">
                          Pros:
                        </span>{" "}
                        {opt.pros.join("; ")}
                      </p>
                    )}
                    {opt.cons.length > 0 && (
                      <p className="font-sans text-xs text-bw-ink-muted">
                        <span className="font-semibold text-bw-navy">
                          Cons:
                        </span>{" "}
                        {opt.cons.join("; ")}
                      </p>
                    )}
                  </div>
                ))}
            </CardContent>
          </Card>
        );
      })}
      <p className="font-sans text-xs italic text-bw-ink-muted">
        Missing an option? Just name it in the chat.
      </p>
    </div>
  );
}

function SignalRow({
  signal,
  label,
  closest,
}: {
  signal: PreferenceSignal;
  label: string;
  // The nearest listed option's label, for a stance in their own words.
  closest?: string;
}) {
  // An honest "I don't know" is a first-class outcome — render it affirmingly
  // (and without conviction pips, which would read as a zero-score judgment),
  // not as a thin entry that pressures people into guessing.
  const unsure = signal.stance === "unsure";
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <span className="font-sans text-xs font-semibold text-bw-navy">
          {label}
        </span>
        {!unsure && <ConvictionPips value={signal.conviction} />}
      </div>
      <span className="font-sans text-xs capitalize text-bw-ink-muted">
        {unsure
          ? "No stance — noted, that's useful data"
          : (signal.stance?.replace(/_/g, " ") ?? "")}
        {/* Scope stays attached to the stance line — "keep · on migrations"
            reads as one conditional position, not a stance plus a footnote. */}
        {signal.conditions && (
          <span className="normal-case"> · {signal.conditions}</span>
        )}
        {signal.priority && (
          <span className="normal-case"> · priority {signal.priority}</span>
        )}
      </span>
      {closest && (
        <span className="font-sans text-[11px] text-bw-ink-muted">
          In your words; closest listed option: {closest}
        </span>
      )}
      {(signal.parameters?.length ?? 0) > 0 && (
        <span className="flex flex-wrap gap-1">
          {/* Stored VARIANT rows are untrusted — a junk item must not crash the page. */}
          {signal
            .parameters!.filter((p) => p && typeof p.name === "string")
            .map((p, i) => (
              <span
                key={i}
                className="rounded-pill bg-bw-blue-muted/20 px-1.5 py-0.5 font-mono text-[10px] text-bw-navy"
              >
                {p.name}: {p.value}
              </span>
            ))}
        </span>
      )}
      {signal.rationale && (
        <span className="font-sans text-xs text-bw-ink-muted">
          “{signal.rationale}”
        </span>
      )}
    </div>
  );
}

const FAMILIARITY_LABELS: Record<string, string> = {
  hands_on: "hands-on",
  familiar: "familiar",
  new_to_this: "new to this",
};

/**
 * The running record of what's been captured — the user-facing proof that
 * their opinion actually landed. Grouped per decision point with an explicit
 * covered / not-covered state, so "what has it got so far?" is answerable at
 * a glance rather than inferable from a flat signal list.
 */
function TakeSoFar({
  signals,
  summary,
  decisionPoints,
}: {
  signals: PreferenceSignal[];
  summary: string | null;
  decisionPoints: DecisionPoint[];
}) {
  const labelFor = useMemo(
    () => makeSignalLabeler(decisionPoints),
    [decisionPoints],
  );
  const closestFor = useMemo(() => makeClosestLabeler(decisionPoints), [decisionPoints]);
  // Memoized: the parent re-renders per keystroke, and React Query's
  // structural sharing keeps these props referentially stable between
  // interview turns — so this recomputes only when a turn actually lands.
  const { active, covered, stray, allCovered } = useMemo(() => {
    // Withdrawn stances vanish from the panel entirely — the user asked for
    // them gone, and watching the row disappear is the proof it worked (the
    // same "prove it logged" principle, in reverse). Coverage recomputes from
    // what's left, honestly.
    const activeSignals = signals.filter((s) => s.stance !== "retract");
    const known = new Set(decisionPoints.map((dp) => dp.key));
    const coveredSet = new Set(
      activeSignals
        .map((s) => s.decision_point_key)
        .filter((k) => known.has(k)),
    );
    return {
      active: activeSignals,
      covered: coveredSet,
      // Signals for retired/unknown decision-point keys (taxonomy drift) stay
      // visible at the end rather than silently vanishing.
      stray: activeSignals.filter((s) => !known.has(s.decision_point_key)),
      allCovered:
        decisionPoints.length > 0 && coveredSet.size === decisionPoints.length,
    };
  }, [decisionPoints, signals]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Your take so far</CardTitle>
        {active.length > 0 && (
          <p
            className={cn(
              "flex items-center gap-1 font-sans text-xs",
              allCovered ? "font-semibold text-bw-navy" : "text-bw-ink-muted",
            )}
          >
            {allCovered && (
              <CheckCircle2 className="h-3.5 w-3.5 text-bw-blue" aria-hidden />
            )}
            {covered.size} of {decisionPoints.length} decision point
            {decisionPoints.length === 1 ? "" : "s"} covered
          </p>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-4 pt-0">
        {active.length === 0 && (
          <p className="font-sans text-sm text-bw-ink-muted">
            Nothing recorded yet. When you're done, you'll review what gets
            recorded, and only what you approve is kept.
          </p>
        )}
        {/* Stances tile across the width; the summary below stays a single
            readable column. Full-bleed prose is as bad as the skinny rail was,
            just in the other direction. */}
        <div className="grid gap-x-8 gap-y-4 md:grid-cols-2 lg:grid-cols-3">
          {active.length > 0 &&
            decisionPoints.map((dp) => {
              const dpSignals = active.filter(
                (s) => s.decision_point_key === dp.key,
              );
              return (
                <div key={dp.key} className="flex flex-col gap-2">
                  {/* Single-DP topics: the group header is redundant noise. */}
                  {decisionPoints.length > 1 && (
                    <span className="flex items-center gap-1 font-sans text-[11px] font-semibold uppercase tracking-wide text-bw-ink-muted">
                      {dpSignals.length > 0 && (
                        <Check className="h-3 w-3 text-bw-blue" aria-hidden />
                      )}
                      {dp.title}
                    </span>
                  )}
                  {dpSignals.length === 0 ? (
                    <span className="font-sans text-xs italic text-bw-ink-muted">
                      Not covered yet
                    </span>
                  ) : (
                    dpSignals.map((s, i) => (
                      <SignalRow
                        key={i}
                        signal={s}
                        label={labelFor(s)}
                        closest={closestFor(s)}
                      />
                    ))
                  )}
                </div>
              );
            })}
          {stray.map((s, i) => (
            <div key={`stray-${i}`}>
              <SignalRow signal={s} label={labelFor(s)} />
            </div>
          ))}
        </div>
        {summary && (
          <div className="flex max-w-3xl flex-col gap-1 border-t border-bw-blue-muted/30 pt-3">
            {/* Not "in your words" — this is the interviewer's summary of the
                conversation, written back in second person. Claiming it as the
                user's own words overstates it, and this is a product about
                capturing what people actually think. */}
            <span className="font-sans text-xs font-semibold uppercase tracking-wide text-bw-ink-muted">
              Your take, summarized
            </span>
            <p className="whitespace-pre-wrap font-sans text-sm text-bw-ink">
              {summary}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * What the end-of-topic analysis proposes, for the person to approve. Nothing
 * from the conversation is on the record until they decide on every stance:
 * approve what's right, reject what isn't. To change one, they reject it and
 * say what they meant in the chat, which makes the draft stale and asks for a
 * fresh review.
 */
function ReviewPanel({
  review,
  decisionPoints,
  filing,
  reviewing,
  failed,
  onFile,
  onReview,
}: {
  review: TopicReview;
  decisionPoints: DecisionPoint[];
  filing: boolean;
  reviewing: boolean;
  failed: boolean;
  onFile: (decisions: { stance_id: string; decision: StanceDecisionKind }[]) => void;
  onReview: () => void;
}) {
  const labelFor = useMemo(() => makeSignalLabeler(decisionPoints), [decisionPoints]);
  const closestFor = useMemo(() => makeClosestLabeler(decisionPoints), [decisionPoints]);
  const titles = useMemo(
    () => new Map(decisionPoints.map((dp) => [dp.key, dp.title])),
    [decisionPoints],
  );
  // A fresh draft starts undecided: the parent keys this panel on the draft.
  const [decisions, setDecisions] = useState<Record<string, StanceDecisionKind>>({});
  const stances = review.stances.filter((s) => s.stance_id);
  const allDecided = stances.every((s) => decisions[s.stance_id!]);

  if (review.stale) {
    return (
      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-6">
          <p className="font-sans text-sm text-bw-ink-muted">
            You've answered again since this review was drafted.
          </p>
          <Button onClick={onReview} disabled={reviewing}>
            {reviewing ? "Reviewing…" : "Review again"}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Review what gets recorded</CardTitle>
        <p className="font-sans text-sm text-bw-ink-muted">
          Nothing from this conversation is on the record yet. Approve each
          stance that says what you meant and reject any that doesn't; only what
          you approve is kept. To change one, reject it and say what you meant
          in the chat.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 pt-0">
        {stances.length === 0 && (
          <p className="font-sans text-sm text-bw-ink-muted">
            No stances were found in this conversation.
          </p>
        )}
        {stances.map((s) => {
          const id = s.stance_id!;
          const decided = decisions[id];
          const closest = closestFor(s);
          return (
            <div
              key={id}
              className="flex flex-col gap-1 rounded-card border border-bw-blue-muted/40 p-3"
              data-testid="proposed-stance"
            >
              <span className="font-sans text-[11px] font-semibold uppercase tracking-wide text-bw-ink-muted">
                {titles.get(s.decision_point_key) ?? s.decision_point_key}
              </span>
              <span className="font-sans text-sm text-bw-navy">
                <span className="font-semibold">{labelFor(s)}</span>{" "}
                <span className="text-bw-ink-muted">
                  · {STANCE_WORDS[s.stance] ?? s.stance}
                  {s.conditions && <> · {s.conditions}</>}
                  {s.priority && <> · priority {s.priority}</>}
                </span>
              </span>
              {closest && (
                <span className="font-sans text-xs text-bw-ink-muted">
                  In your words; closest listed option: {closest}
                </span>
              )}
              {s.refers_to && (
                <span className="font-sans text-xs text-bw-ink-muted">
                  Agreeing with: “{s.refers_to}”
                </span>
              )}
              {s.quote && (
                <span className="font-sans text-xs italic text-bw-ink">
                  You said: “{s.quote}”
                </span>
              )}
              <div className="mt-1 flex gap-2">
                <Button
                  size="sm"
                  variant={decided === "approve" ? "default" : "outline"}
                  aria-pressed={decided === "approve"}
                  onClick={() => setDecisions((d) => ({ ...d, [id]: "approve" }))}
                >
                  <Check className="mr-1 h-3.5 w-3.5" aria-hidden /> Approve
                </Button>
                <Button
                  size="sm"
                  variant={decided === "reject" ? "default" : "outline"}
                  aria-pressed={decided === "reject"}
                  onClick={() => setDecisions((d) => ({ ...d, [id]: "reject" }))}
                >
                  <X className="mr-1 h-3.5 w-3.5" aria-hidden /> Reject
                </Button>
              </div>
            </div>
          );
        })}
        {failed && (
          <p className="font-sans text-sm text-bw-ink-muted" role="alert">
            Couldn't save that. Review again and retry.
          </p>
        )}
        <div className="flex flex-wrap items-center justify-end gap-3">
          {stances.length > 0 && (
            <Button
              variant="outline"
              onClick={() =>
                setDecisions(Object.fromEntries(stances.map((s) => [s.stance_id!, "approve" as const])))
              }
            >
              Approve all
            </Button>
          )}
          <Button
            onClick={() =>
              onFile(stances.map((s) => ({ stance_id: s.stance_id!, decision: decisions[s.stance_id!] })))
            }
            disabled={!allDecided || filing}
          >
            {filing
              ? "Saving…"
              : stances.length === 0
                ? "Finish without recording anything"
                : "Record what I approved"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Route element. The interview body is remounted per topic (``key``): React
 * Router reuses the element across ``/preferences/:topic`` navigations, and a
 * keyed remount structurally resets every piece of topic-scoped state — the
 * draft, the optimistic bubble, the acknowledgements, the reopen latch, and
 * the mutations' ``isPending`` — with no reset checklist to maintain and no
 * stale-callback guards. Late responses for the previous topic can't touch
 * the new mount: React ignores state updates from an unmounted component,
 * while the hook-level cache invalidations in ``usePreferences`` still run.
 */
export function PreferencesTopicPage() {
  const { topic = "" } = useParams();
  return <TopicInterview key={topic} topic={topic} />;
}

function TopicInterview({ topic }: { topic: string }) {
  const { data, isLoading, isError, error } = useTopic(topic);
  // For the "next topic" nudge after finishing — the best not-done topic for
  // this person (excluding this one): their own ranking first, catalog order
  // for anything unranked. Cached; shares the selection query.
  const topics = useTopics();
  const nextTopic = (topics.data ?? [])
    .flatMap((g) => g.topics)
    .filter((t) => t.key !== topic && t.status !== "done")
    .sort((a, b) => (a.recommended_rank ?? Infinity) - (b.recommended_rank ?? Infinity))[0];

  const start = useStartTopic(topic);
  const send = useSendMessage(topic);
  const review = useReviewTopic(topic);
  const file = useFileStances(topic);

  const [input, setInput] = useState("");
  const [pending, setPending] = useState<string | null>(null);
  // An answer records nothing, so there is no "Logged: …" to show: the
  // review at the end is where they see (and approve) what gets recorded.
  // Parts of an answer that read as about a different actor than the question
  // are worth a word now, while the conversation is still going.
  const [justClarify, setJustClarify] = useState<string[]>([]);
  // Feedback about the catalog itself ("your cons for this option
  // are out of date"): it isn't a stance, so it never appears in the take panel
  // — without an ack, telling us the catalog is wrong looks like it went nowhere.
  const [justFlagged, setJustFlagged] = useState<string[]>([]);
  const loggedTimer = useRef<number | null>(null);
  // Deliberate reopen of a finished topic — the composer is otherwise locked when
  // done so a stray keystroke can't silently reopen it and clear the summary.
  const [reopened, setReopened] = useState(false);
  // One auto-start per mount (and the mount is per-topic, via the key above).
  const startedRef = useRef(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const busy = start.isPending || send.isPending || review.isPending || file.isPending;

  // Re-lock whenever the topic settles as done (fresh finish or an
  // auto-completed interview) — reopening is always an explicit click.
  useEffect(() => {
    if (data?.status === "done") setReopened(false);
  }, [data?.status]);

  // The ack timer must not fire into an unmounted component.
  useEffect(() => {
    return () => {
      if (loggedTimer.current !== null)
        window.clearTimeout(loggedTimer.current);
    };
  }, []);

  // Generate the opening turn once per fresh topic. Skip on error so a failed
  // start can be retried (otherwise we'd pin the user to a blank chat).
  useEffect(() => {
    if (
      data &&
      data.status === "not_started" &&
      !startedRef.current &&
      !start.isPending &&
      !start.isError
    ) {
      startedRef.current = true;
      start.mutate();
    }
  }, [data, start]);

  // Keep the conversation scrolled to the latest turn.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight });
  }, [data?.transcript.length, pending, send.isPending]);

  // The previous turn's acks are stale the moment a new answer is in flight.
  const clearAcks = useCallback(() => {
    setJustClarify([]);
    setJustFlagged([]);
    if (loggedTimer.current !== null) {
      window.clearTimeout(loggedTimer.current);
      loggedTimer.current = null;
    }
  }, []);

  // Shared by both channels: a spoken answer and a typed one produce the same
  // acknowledgements, because they went through the same endpoint.
  // ``mutate`` is stable across renders; the mutation object is not.
  const startReview = review.mutate;
  const applyResult = useCallback(
    (result: MessageResult) => {
      // Covered: draft what gets recorded, for them to approve.
      if (result.ready_for_review) startReview();
      const flagged = result.taxonomy_feedback ?? [];
      const clarify = Array.from(new Set((result.clarify ?? []).map((c) => c.why)));
      if (clarify.length === 0 && flagged.length === 0) return;
      setJustClarify(clarify);
      setJustFlagged(flagged);
      if (loggedTimer.current !== null) window.clearTimeout(loggedTimer.current);
      loggedTimer.current = window.setTimeout(() => {
        setJustClarify([]);
        setJustFlagged([]);
      }, 8000);
    },
    [startReview],
  );

  function submit(message: string) {
    const content = message.trim();
    // Gate on any pending op (start or send), not just send — avoids racing the
    // opening turn.
    if (!content || busy) return;
    setInput("");
    setPending(content);
    clearAcks();
    send.mutate(
      { content },
      {
        onSuccess: applyResult,
        // Restore the text if the send fails so the user doesn't lose what they typed.
        onError: () => setInput(content),
        onSettled: () => setPending(null),
      },
    );
  }

  // --- Voice --------------------------------------------------------------
  // The spoken path posts to the very same endpoint as the composer above, with
  // one flag set. Everything a turn does — keeping the answer verbatim,
  // coverage, catalog feedback, the ready-for-review judgement — therefore
  // applies identically, because it is literally the same turn.
  const voiceConfig = useVoiceConfig();
  const transport = useVoiceTransport();

  const handleUtterance = useCallback(
    async (text: string) => {
      // Same gate the composer applies, for the same reason — but the two
      // channels can genuinely collide here in a way two typed sends can't.
      // Someone types an answer and hits Enter, then the VAD grace window
      // closes on something they said a second earlier, and both POST to
      // /messages for the same topic at once. Each turn then extracts signals
      // from a transcript missing the other, and both replies answer the same
      // question. Throwing hands control back to the voice loop, which speaks
      // an apology and reopens the mic — a path it already has.
      if (busy) throw new Error("A previous answer is still being sent.");
      setPending(text);
      clearAcks();
      try {
        const result = await send.mutateAsync({ content: text, voice: true });
        applyResult(result);
        // Covered ends the spoken session: what comes next is the review.
        return { reply: result.assistant_text, done: result.ready_for_review === true };
      } finally {
        setPending(null);
      }
    },
    [applyResult, busy, clearAcks, send],
  );

  // Read the question that's already on screen when voice starts, so the first
  // thing the driver hears is what they're being asked.
  const lastAssistantTurn = useCallback(() => {
    const turns = data?.transcript ?? [];
    for (let i = turns.length - 1; i >= 0; i--) {
      if (turns[i].role === "assistant") return turns[i].content;
    }
    return null;
  }, [data?.transcript]);

  const voice = useVoiceSession(
    transport,
    voiceConfig.data?.sample_rate ?? 16000,
    handleUtterance,
    lastAssistantTurn,
  );

  if (isError) {
    return (
      <div className="flex flex-col gap-4">
        <BackLink />
        <p className="font-sans text-sm text-bw-ink-muted">
          {Number(error?.status) === 404
            ? "That topic isn't available."
            : "Couldn't load this topic. Try again shortly."}
        </p>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="flex flex-col gap-4">
        <BackLink />
        <p className="font-sans text-sm text-bw-ink-muted">Loading…</p>
      </div>
    );
  }

  // Lock the composer while busy, and when finished until the user explicitly
  // reopens — a stray message would otherwise clear the saved summary.
  const done = data.status === "done";
  const locked = busy || (done && !reopened);
  // The opening greeting is one assistant turn; only allow finishing once the
  // user has actually answered (the backend 400s otherwise).
  const hasUserTurn = data.transcript.some((t) => t.role === "user");

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <BackLink />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-bw-navy">{data.title}</h1>
            <span className="rounded-pill bg-bw-blue-muted/20 px-3 py-1 font-sans text-xs font-semibold text-bw-navy">
              {STATUS_LABEL[data.status]}
            </span>
            {data.familiarity && FAMILIARITY_LABELS[data.familiarity] && (
              <span className="rounded-pill bg-bw-blue/10 px-3 py-1 font-sans text-xs text-bw-navy">
                {FAMILIARITY_LABELS[data.familiarity]}
              </span>
            )}
          </div>
          <span className="flex items-center gap-1 font-sans text-xs text-bw-ink-muted">
            <Check className="h-3.5 w-3.5 text-bw-blue" aria-hidden />{" "}
            Auto-saved
          </span>
        </div>
        {/* Who is using the AI in this topic, stated before the prose and in
            the same words the interviewer uses, so a person answering about
            their own chat habits can see that this topic is about our code
            (or the reverse) before they type. */}
        {data.frame && data.frame !== "none" && data.frame_label && (
          <div className="max-w-2xl rounded-card border border-bw-blue/30 bg-bw-blue/5 px-4 py-3">
            <p className="font-sans text-xs font-semibold uppercase tracking-wide text-bw-blue">
              This topic is about: {data.frame_label}
            </p>
            {data.frame_note && (
              <p className="mt-1 font-sans text-sm text-bw-navy">{data.frame_note}</p>
            )}
          </div>
        )}
        <p className="max-w-2xl font-sans text-sm text-bw-ink-muted">
          {data.prose}
        </p>
        {data.purpose && (
          <p className="max-w-2xl font-sans text-sm text-bw-navy">
            <span className="font-semibold">Why we're asking:</span>{" "}
            {data.purpose}
          </p>
        )}
      </div>

      <OptionsBlock
        decisionPoints={data.decision_points}
        topicTitle={data.title}
        topicFrame={data.frame}
      />

      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          {/* Conversation */}
          <div
            ref={scrollRef}
            className="flex max-h-[28rem] flex-col gap-3 overflow-y-auto rounded-card border border-bw-blue-muted/40 p-4"
          >
            {data.transcript.length === 0 && start.isError && (
              <div className="flex items-center justify-between gap-2 font-sans text-sm text-bw-ink-muted">
                <span>Couldn't start the interview.</span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    startedRef.current = true;
                    start.mutate();
                  }}
                >
                  Retry
                </Button>
              </div>
            )}
            {data.transcript.length === 0 && !busy && !start.isError && (
              <p className="font-sans text-sm text-bw-ink-muted">
                Starting the interview…
              </p>
            )}
            {data.transcript.map((turn, i) => (
              <Bubble key={i} role={turn.role} text={turn.content} />
            ))}
            {pending && <Bubble role="user" text={pending} />}
            {busy && (
              <div className="flex items-center gap-2 self-start font-sans text-sm text-bw-ink-muted">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />{" "}
                Thinking…
              </div>
            )}
          </div>

          {/* The answer sounded like it was about someone else's use of AI
              than the question asked about: worth saying which they meant. */}
          {justClarify.length > 0 && (
            <div
              className="flex items-start gap-1.5 font-sans text-xs text-bw-ink-muted"
              role="status"
            >
              <MessageSquareWarning
                className="mt-0.5 h-3.5 w-3.5 shrink-0 text-bw-blue"
                aria-hidden
              />
              <span>
                <span className="font-semibold text-bw-navy">Which did you mean?</span>{" "}
                {justClarify.join(" · ")}
              </span>
            </div>
          )}

          {/* Feedback on the catalog itself gets its own ack — it won't turn up
              in the take panel, so this is the only proof it landed. */}
          {justFlagged.length > 0 && (
            <div
              className="flex items-start gap-1.5 font-sans text-xs text-bw-ink-muted"
              role="status"
            >
              <MessageSquareWarning
                className="mt-0.5 h-3.5 w-3.5 shrink-0 text-bw-blue"
                aria-hidden
              />
              <span>
                <span className="font-semibold text-bw-navy">
                  Flagged for the catalog:
                </span>{" "}
                {justFlagged.join(" · ")}
              </span>
            </div>
          )}

          {/* Hands-free channel into the same conversation. Hidden entirely
              when the deployment has no ElevenLabs key — offering a button that
              503s is worse than not offering one. */}
          {voiceConfig.data?.enabled && (
            <VoiceInterview
              session={voice}
              disabled={locked}
              disabledReason={
                done && !reopened
                  ? "This topic is finished — reopen it to add more by voice."
                  : undefined
              }
            />
          )}

          {/* Input + typed rationale (qual) */}
          <form
            className="flex items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              submit(input);
            }}
          >
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={locked}
              maxLength={4000}
              onKeyDown={(e) => {
                // Ignore Enter while an IME is composing (CJK input), else it
                // submits mid-composition instead of confirming the character.
                if (
                  e.key === "Enter" &&
                  !e.shiftKey &&
                  !e.nativeEvent.isComposing
                ) {
                  e.preventDefault();
                  submit(input);
                }
              }}
              rows={2}
              placeholder="Type your answer — or brain-dump everything you think about this topic, and it'll get sorted… (Enter to send, Shift+Enter for a newline)"
              className="flex-1 resize-none rounded-card border border-bw-blue-muted/40 px-3 py-2 font-sans text-sm text-bw-ink focus:border-bw-blue focus:outline-none disabled:opacity-50"
            />
            <Button
              type="submit"
              size="icon"
              disabled={locked || !input.trim()}
              aria-label="Send"
            >
              <Send className="h-4 w-4" />
            </Button>
          </form>

          <div className="flex items-center justify-end gap-3">
            {done && !reopened && (
              <span className="mr-auto font-sans text-xs text-bw-ink-muted">
                Completed — your take is saved.
              </span>
            )}
            {done && !reopened ? (
              <>
                {nextTopic && (
                  <Link
                    to={`/preferences/${nextTopic.key}`}
                    className="rounded-card bg-bw-navy px-4 py-2 font-sans text-sm font-semibold text-white transition-colors hover:bg-bw-navy/90"
                  >
                    Next topic: {nextTopic.title} →
                  </Link>
                )}
                <Button variant="outline" onClick={() => setReopened(true)}>
                  Reopen to add more
                </Button>
              </>
            ) : (
              <Button
                variant="secondary"
                onClick={() => review.mutate()}
                disabled={locked || !hasUserTurn}
              >
                {review.isPending ? "Reviewing…" : "I'm done — review what gets recorded"}
              </Button>
            )}
          </div>
        </div>

        {data.review && (
          <ReviewPanel
            key={data.review.drafted_at ?? "draft"}
            review={data.review}
            decisionPoints={data.decision_points}
            filing={file.isPending}
            reviewing={review.isPending}
            failed={file.isError}
            onReview={() => review.mutate()}
            // Explicit re-lock for the reopen → file path, where status stays
            // done throughout and the status-change effect can't fire.
            onFile={(decisions) => file.mutate(decisions, { onSuccess: () => setReopened(false) })}
          />
        )}
        {review.isError && (
          <p className="font-sans text-sm text-bw-ink-muted" role="alert">
            Couldn't draft the review. Try again shortly.
          </p>
        )}

        <TakeSoFar
          signals={data.signals}
          summary={data.summary}
          decisionPoints={data.decision_points}
        />
      </div>
    </div>
  );
}

function Bubble({ role, text }: { role: "user" | "assistant"; text: string }) {
  const isUser = role === "user";
  return (
    <div
      className={cn(
        "max-w-[85%] whitespace-pre-wrap rounded-card px-3 py-2 font-sans text-sm",
        isUser
          ? "self-end bg-bw-navy text-white"
          : "self-start bg-bw-blue-muted/15 text-bw-ink",
      )}
    >
      {text}
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/preferences"
      className="flex w-fit items-center gap-1 font-sans text-sm text-bw-ink-muted hover:text-bw-navy"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden /> All topics
    </Link>
  );
}
